import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import { fetchWorkspaceMembers } from "services/workspaceService";

const activeStatuses = new Set(["ACTIVE", "ACCEPTED"]);

export const getWorkspaceMemberUser = (membership = {}) => {
  const user = membership.user || membership.profile || membership.member || {};
  const userId =
    user.id ||
    membership.userId ||
    membership.memberUserId ||
    membership.profileId ||
    "";

  if (!userId && !user.email && !user.username) return null;

  return {
    ...user,
    id: userId || user.id,
    firstName: user.firstName || membership.firstName || "",
    middleName: user.middleName || membership.middleName || "",
    lastName: user.lastName || membership.lastName || "",
    name: user.name || membership.userName || membership.name || "",
    fullName: user.fullName || membership.fullName || "",
    username: user.username || membership.username || "",
    email:
      user.email ||
      user.contactDetails?.personalEmail ||
      membership.email ||
      membership.userEmail ||
      "",
    profilePhoto:
      user.profilePhoto ||
      user.avatar ||
      membership.profilePhoto ||
      membership.avatar ||
      "",
  };
};

export const getWorkspaceMemberDisplayName = (user = {}) => {
  const composed = [user.firstName, user.middleName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    composed ||
    user.fullName ||
    user.name ||
    user.username ||
    user.email ||
    user.id ||
    "User"
  );
};

export const getWorkspaceMemberEmail = (user = {}) =>
  user.email ||
  user.contactDetails?.personalEmail ||
  user.contactDetails?.workEmail ||
  user.personalEmail ||
  user.workEmail ||
  "";

export const isActiveWorkspaceMember = (membership = {}) => {
  const status = String(
    membership.membershipStatus || membership.status || membership.invitationStatus || "",
  ).toUpperCase();

  return !status || activeStatuses.has(status);
};

const uniqueUsers = (memberships = []) => {
  const seen = new Set();

  return memberships
    .filter(isActiveWorkspaceMember)
    .map(getWorkspaceMemberUser)
    .filter((user) => user?.id)
    .filter((user) => {
      if (seen.has(user.id)) return false;
      seen.add(user.id);
      return true;
    });
};

const userSearchText = (user = {}) =>
  [
    getWorkspaceMemberDisplayName(user),
    getWorkspaceMemberEmail(user),
    user.username,
    user.id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const useWorkspaceMembers = ({ enabled = true, size = 200 } = {}) => {
  const { user } = useAuth();
  const { selectedWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const workspaceId = selectedWorkspace?.id || "";

  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadMembers = useCallback(async () => {
    if (!enabled || !actorId || !workspaceId) {
      setMemberships([]);
      return [];
    }

    try {
      setLoading(true);
      setError(null);
      const page = await fetchWorkspaceMembers({
        actorId,
        workspaceId: workspaceId,
        size,
      });
      const content = Array.isArray(page?.content) ? page.content : [];
      setMemberships(content);
      return content;
    } catch (err) {
      setError(err);
      setMemberships([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [actorId, enabled, size, workspaceId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const users = useMemo(() => uniqueUsers(memberships), [memberships]);

  const searchUsers = useCallback(
    (query = "", { minLength = 0, limit = 25 } = {}) => {
      const term = String(query || "").trim().toLowerCase();
      if (term.length < minLength) return [];

      const source = term
        ? users.filter((memberUser) => userSearchText(memberUser).includes(term))
        : users;

      return source.slice(0, limit);
    },
    [users],
  );

  return {
    actorId,
    error,
    loading,
    memberships: memberships.filter(isActiveWorkspaceMember),
    refresh: loadMembers,
    searchUsers,
    selectedWorkspace,
    users,
    workspaceId,
  };
};

export default useWorkspaceMembers;
